/**
 * BoothAttract — idle attract mode inside Booth OS.
 *
 * After 45 seconds of inactivity on the launcher the kiosk slips
 * into a quiet attract surface: a single guest photo cross-fades
 * with the next every ~7 seconds while a rotating editorial prompt
 * whispers in italic gold. Touch anywhere returns instantly to the
 * launcher. If the event has no photos yet, only the prompts cycle.
 *
 * Hard locks (per Mode 1 constitution):
 *   • No countdowns / counters / fade-in messages with "Welcome!"
 *   • No carousel arrows, no dot indicators.
 *   • No "screensaver" text, no clock.
 *   • Slow cinematic motion only — opacity cross-fades, no swiping
 *     transforms, no parallax.
 *
 * This is atmospheric invitation, not screensaver software.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { SERIF } from './primitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHOTO_DWELL_MS = 7000;
const PROMPT_DWELL_MS = 9000;

const PROMPTS = [
  'Leave a message for the morning ✦',
  'The room is gathering ✦',
  'Add your place to the night ✦',
  'A photograph, a few words, a memory ✦',
  'Touch to begin ✦',
];

const BoothAttract = ({ theme, event, onWake }) => {
  const [photos, setPhotos] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [promptIndex, setPromptIndex] = useState(0);
  // 'in' | 'out' — drives the cross-fade between consecutive photos.
  const [phase, setPhase] = useState('in');
  const mountedRef = useRef(true);
  const wakeFiredRef = useRef(false);

  // iter213 ✦ production rehearsal — Booth OS screensaver wouldn't
  // dismiss reliably on tap (iOS Safari in fullscreen swallowed bare
  // `pointerdown` on the background-image layer). Belt-and-braces:
  //   • Idempotent: a touch that fires both `pointerdown` and
  //     `click` (or `touchend`) must not transition twice.
  //   • Multiple event types so whichever channel iOS Safari
  //     delivers, the wake fires.
  //   • Reset on unmount so a re-entry into attract is clean.
  const handleWake = (e) => {
    if (wakeFiredRef.current) return;
    wakeFiredRef.current = true;
    // Prevent the synthetic click that follows a touchend from
    // landing on the launcher card directly underneath.
    try { e?.preventDefault?.(); } catch (_) {}
    try { onWake?.(); } catch (_) {}
  };

  // ── Fetch event photos once on mount. If the night is brand new
  //    (zero uploads), the prompts alone carry the surface. ───────
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${event.event_id}/photos`);
        if (!mountedRef.current) return;
        // Filter to still photos (skip booth-360 placeholders) and
        // build a stable list of resolvable URLs.
        const list = (r.data || [])
          .filter((p) => p.media_type !== 'video' && p.storage_path)
          .slice(0, 60)
          .map((p) => `${BACKEND_URL}/api/photos/${p.storage_path}`);
        setPhotos(list);
      } catch (_) {
        // Silent — attract mode degrades gracefully to prompts-only.
      }
    })();
    return () => { mountedRef.current = false; };
  }, [event.event_id]);

  // ── Photo cycler with cross-fade. ─────────────────────────────
  useEffect(() => {
    if (photos.length <= 1) return;
    const id = setInterval(() => {
      setPhase('out');
      setTimeout(() => {
        if (!mountedRef.current) return;
        setPhotoIndex((i) => (i + 1) % photos.length);
        setPhase('in');
      }, 1100);
    }, PHOTO_DWELL_MS);
    return () => clearInterval(id);
  }, [photos.length]);

  // ── Prompt cycler. ────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      setPromptIndex((i) => (i + 1) % PROMPTS.length);
    }, PROMPT_DWELL_MS);
    return () => clearInterval(id);
  }, []);

  const currentPhoto = photos[photoIndex];

  return (
    <div
      data-testid="booth-attract"
      onPointerDown={handleWake}
      onTouchEnd={handleWake}
      onClick={handleWake}
      role="button"
      aria-label="Tap to begin"
      style={{
        position: 'absolute', inset: 0,
        background: theme.bg,
        cursor: 'pointer',
        overflow: 'hidden',
        // iter213 — iOS Safari won't dispatch synthesised events
        // reliably on tap-to-zoom-eligible elements. `manipulation`
        // disables the double-tap-zoom heuristic, so pointer/touch
        // events fire instantly with no 300ms delay.
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        animation: 'editorialFade 800ms ease-out',
      }}
    >
      {/* Photo layer — slow zoom + opacity cross-fade. */}
      {currentPhoto && (
        <div
          key={`${photoIndex}-${phase}`}
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${currentPhoto})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.42) saturate(0.85)',
            opacity: phase === 'in' ? 1 : 0,
            transform: phase === 'in' ? 'scale(1.02)' : 'scale(1.05)',
            transition: 'opacity 1100ms ease, transform 7000ms linear',
            // iter213 — the photo layer must not eat the tap. The
            // parent <div> owns the wake gesture; this layer is
            // visual chrome only.
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Atmospheric overlay — restrained walnut wash that keeps the
          editorial palette intact even on warm photos. */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(1200px 800px at 50% 30%, transparent 0%, ${theme.bg}E6 65%, ${theme.bg} 100%)`,
        pointerEvents: 'none',
      }} />
      {/* Centred editorial copy — one prompt at a time, gold whisper. */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '40px 24px',
        pointerEvents: 'none',
      }}>
        <div style={{
          fontFamily: SERIF, fontWeight: 800,
          fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: theme.gold, marginBottom: 18, opacity: 0.85,
        }}>
          ✦ {event.event_name || 'TONIGHT'}
        </div>
        <div
          key={promptIndex}
          style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(28px, 4.5vw, 50px)',
            lineHeight: 1.18, letterSpacing: '0.01em',
            color: theme.ink, maxWidth: 720,
            animation: 'attractWhisper 1400ms ease-out',
          }}
        >
          {PROMPTS[promptIndex]}
        </div>
      </div>
      <style>{`
        @keyframes attractWhisper {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default BoothAttract;
